import React from "react";
import { Datum } from "plotly.js";
import BasePlot, {
  BasePlotProps,
  barChartColors,
  basePlotlyConfig,
} from "features/Dashboards/Base/BasePlot";
import DashboardSimpleNumber from "features/Dashboards/Base/DashboardSimpleNumber";
import { PlotlyTrace } from "api-client";
import { GWP_M2_AREA_UNIT } from "utils";

// Fresh config per call so sibling bar blocks don't share (and stomp) the same
// nested layout object when setting range / autorange.
const makeBarConfig = (): BasePlotProps =>
  basePlotlyConfig({
    height: 160,
    barmode: "relative",
    legend: { orientation: "h", y: -0.3 },
    margin: { l: 0, r: 0, b: 10, t: 0 },
    yaxis: { visible: false },
    xaxis: {
      tickmode: "auto",
      zeroline: true,
      zerolinewidth: 1,
      zerolinecolor: "rgba(107, 114, 128, 0.4)",
    },
  });

interface ModuleBarBlockProps {
  title: string;
  total: number | null | undefined;
  traces: Array<PlotlyTrace>;
  moduleListTooltip?: string;
  plotXRange?: number[];
  autorange?: boolean;
}

/** A labelled sub-block: big total on the left, transposed module bar on the right. */
export default function ModuleBarBlock(props: ModuleBarBlockProps) {
  const config: BasePlotProps = {
    ...makeBarConfig(),
    data: props.traces.map((point, index) => ({
      // transpose: API returns x = modules, y = values
      x: (Array.isArray(point.y) ? point.y : []) as Datum[],
      y: (Array.isArray(point.x) ? point.x : []) as Datum[],
      name: point.name,
      orientation: "h",
      type: "bar",
      width: 0.2,
      marker: { color: barChartColors[index % barChartColors.length] },
      hovertemplate: `%{x} <span>kgCO<sub>2</sub>e/m<sup>2</sup><sub>NRF</sub>*a</span>`,
    })) as Partial<Plotly.Data>[],
  };

  if (config.layout?.xaxis) {
    if (props.autorange) {
      config.layout.xaxis.autorange = true;
    } else if (props.plotXRange) {
      config.layout.xaxis.range = props.plotXRange;
    }
  }

  return (
    <div className="rounded-lg bg-gray-100 text-gray-500 p-5 flex flex-col h-full">
      <h3 className="text-xs font-bold uppercase text-gray-400">
        {props.title}
      </h3>
      <div className="flex mb-3 h-[160px]">
        <div className="flex w-1/3 text-center items-center">
          <DashboardSimpleNumber
            value={props.total}
            unit={GWP_M2_AREA_UNIT}
            tooltip={props.moduleListTooltip}
          />
        </div>
        <div className="flex w-2/3">
          <BasePlot {...config} />
        </div>
      </div>
    </div>
  );
}
